import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { classNames } from "utils";
import { useMediaQuery } from "react-responsive";

import getIcon from "components/Icons";
import { LinkButton } from "components/Buttons";
import Image from "components/Image";

import * as styles from "./SectionExploreSolutions.module.less";

import smallCubeUrl from "./small_cube.svg";
import floatingWhiteCubeUrl from "./floating_white_cube.svg";

/**
 * @param {GatsbyTypes.SolutionsFragment & { className: string; }} props
 */
function SolutionCard({
  image: { image, alt, caption },
  name,
  description,
  solutionLink: { linkText, linkIcon },
  solutionURL,
  className: customClassname,
}) {
  return (
    <article className={classNames(styles.card, customClassname)}>
      <Image
        src={image.childImageSharp}
        title={caption}
        alt={alt}
        publicURL={image.publicURL}
        className={styles.mediaImage}
      />
      <div className={styles.cardFooter}>
        <div className={styles.cardText}>
          <h4>{name}</h4>
          <p>{description}</p>
        </div>
        <LinkButton
          className={styles.cardButton}
          fullWidth
          to={solutionURL}
          isContent
          title={linkText}
          // @ts-expect-error
          icon={getIcon(linkIcon, { width: 30 })}
        />
      </div>
    </article>
  );
}

export const SolutionsFragment = graphql`
  fragment Solutions on MarkdownRemarkFrontmatterSolutions {
    image {
      image {
        childImageSharp {
          fluid(maxWidth: 384, maxHeight: 302, cropFocus: CENTER, quality: 90) {
            ...GatsbyImageSharpFluid
          }
        }
        extension
        publicURL
      }
      alt
      caption
    }
    name
    description
    solutionLink {
      linkText
      linkIcon
    }
    solutionURL
  }
`;

export default function SectionExploreSolutions() {
  /** @type {GatsbyTypes.SectionExploreSolutionsQuery} */
  const {
    markdownRemark: {
      frontmatter: { title, subtitle, solutions },
    },
  } = useStaticQuery(graphql`
    query SectionExploreSolutions {
      markdownRemark(
        frontmatter: {
          identifier: { eq: "exploreSolutions" }
          type: { eq: "staticSection" }
        }
      ) {
        frontmatter {
          title
          subtitle
          solutions {
            ...Solutions
          }
        }
      }
    }
  `);

  return (
    <SectionExploreSolutionsTemplate
      title={title}
      subtitle={subtitle}
      solutions={solutions}
    />
  );
}

/**
 * @typedef {object} SectionExploreSolutionsTemplateProps
 * @prop {string} title
 * @prop {string} subtitle
 * @prop {readonly GatsbyTypes.SolutionsFragment[]} solutions
 *
 * @param {SectionExploreSolutionsTemplateProps} props
 */
export function SectionExploreSolutionsTemplate({
  title,
  subtitle,
  solutions,
}) {
  const isScreenSmallerThanMedium = useMediaQuery({
    query: "(max-width: 768px)",
  });

  return (
    <section className={styles.section}>
      {!isScreenSmallerThanMedium && (
        <>
          <img
            className={styles.smallCubeImage}
            src={smallCubeUrl}
            alt="Floating white cube"
          />
          <img
            className={styles.floatingWhiteCubeImage}
            src={floatingWhiteCubeUrl}
            alt="Floating white cube"
          />
        </>
      )}
      <h1>{title}</h1>
      <p>{subtitle}</p>
      <div className={styles.solutions}>
        {solutions.map((solution, index) => {
          const styleMap = [
            styles.firstCard,
            styles.secondCard,
            styles.thirdCard,
          ];
          const style = styleMap[index % 3];
          return (
            <SolutionCard
              key={`solutionCard-${index}`}
              className={style}
              {...solution}
            />
          );
        })}
      </div>
    </section>
  );
}
